<template>
  <auth-shell
    :title="$t('auth.register.title')"
    :subtitle="$t('auth.register.sub')"
    :back-label="$t('auth.backHome')"
    icon="fal fa-rocket"
    icon-tone="lime"
    wide
  >
    <form class="ps-auth-form" novalidate @submit.prevent="submitForm(form)">
      <div class="ps-grid">
        <!-- store name -->
        <div class="ps-field">
          <label class="ps-field__label" for="regName">{{
            $t("auth.register.storeName")
          }}</label>
          <input
            id="regName"
            v-model="form.first_name"
            type="text"
            required
            :placeholder="$t('auth.register.storeNamePh')"
            class="ps-input"
            :disabled="overlay"
            @input="$v.form.first_name.$touch()"
            @blur="$v.form.first_name.$touch()"
          />
          <p v-if="first_nameError.length" class="ps-field__error">
            {{ first_nameError[0] }}
          </p>
        </div>

        <!-- email -->
        <div class="ps-field">
          <label class="ps-field__label" for="regEmail">{{
            $t("auth.register.email")
          }}</label>
          <input
            id="regEmail"
            v-model="form.email"
            type="email"
            required
            autocomplete="email"
            :placeholder="$t('auth.register.emailPh', { at: '@' })"
            class="ps-input"
            :disabled="overlay"
            @input="$v.form.email.$touch()"
            @blur="$v.form.email.$touch()"
          />
          <p v-if="emailError.length" class="ps-field__error">
            {{ emailError[0] }}
          </p>
        </div>

        <!-- password -->
        <div class="ps-field">
          <label class="ps-field__label" for="regPw">{{
            $t("auth.register.password")
          }}</label>
          <div class="ps-field__pw">
            <input
              id="regPw"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              required
              :minlength="6"
              autocomplete="new-password"
              :placeholder="$t('auth.register.passwordPh')"
              class="ps-input"
              :disabled="overlay"
              @input="$v.form.password.$touch()"
              @blur="$v.form.password.$touch()"
            />
            <button
              type="button"
              class="ps-field__eye"
              :aria-label="$t('show_password') || 'Show password'"
              @click="showPassword = !showPassword"
            >
              <v-icon small>{{
                showPassword ? "fal fa-eye-slash" : "fal fa-eye"
              }}</v-icon>
            </button>
          </div>
          <p v-if="passwordErrors.length" class="ps-field__error">
            {{ passwordErrors[0] }}
          </p>
        </div>

        <!-- confirm password -->
        <div class="ps-field">
          <label class="ps-field__label" for="regCf">{{
            $t("auth.register.confirm")
          }}</label>
          <div class="ps-field__pw">
            <input
              id="regCf"
              v-model="form.confirm_password"
              :type="shoConfirmPassword ? 'text' : 'password'"
              required
              autocomplete="new-password"
              :placeholder="$t('auth.register.confirmPh')"
              class="ps-input"
              :disabled="overlay"
              @input="$v.form.confirm_password.$touch()"
              @blur="$v.form.confirm_password.$touch()"
            />
            <button
              type="button"
              class="ps-field__eye"
              :aria-label="$t('show_password') || 'Show password'"
              @click="shoConfirmPassword = !shoConfirmPassword"
            >
              <v-icon small>{{
                shoConfirmPassword ? "fal fa-eye-slash" : "fal fa-eye"
              }}</v-icon>
            </button>
          </div>
          <p v-if="confirmPasswordErrors.length" class="ps-field__error">
            {{ confirmPasswordErrors[0] }}
          </p>
        </div>
      </div>

      <!-- terms -->
      <label class="ps-check ps-check--wrap">
        <input v-model="terms" type="checkbox" required class="ps-check__box" />
        <span class="ps-check__text">
          {{ $t("auth.register.agree") }}
          <router-link to="/terms" class="ps-auth-switch__link">{{
            $t("auth.register.terms")
          }}</router-link>
          {{ $t("auth.register.and") }}
          <router-link to="/privacy" class="ps-auth-switch__link">{{
            $t("auth.register.privacy")
          }}</router-link>
        </span>
      </label>

      <button
        type="submit"
        class="ps-btn ps-btn--brand ps-btn--block ps-auth-submit"
        :disabled="overlay"
      >
        {{ overlay ? "…" : $t("auth.register.submit") }}
      </button>
    </form>

    <p class="ps-auth-switch">
      {{ $t("auth.register.haveAccount") }}
      <router-link to="/login" class="ps-auth-switch__link">{{
        $t("auth.register.login")
      }}</router-link>
    </p>

    <app-loading :overlay="overlay" />
  </auth-shell>
</template>

<script>
import { toast } from "@/controllers/alert";
import { Register } from "@/validations/user";
import AuthShell from "@/components/auth/AuthShell.vue";

export default {
  name: "RegisterPage",
  layout: "blank",
  components: { AuthShell },
  mixins: [Register],
  data: () => ({
    terms: false,
    overlay: false,
    showPassword: false,
    shoConfirmPassword: false,
    form: {
      first_name: "",
      email: "",
      password: "",
      confirm_password: "",
    },
  }),
  mounted() {
    try {
      const q = this.$route?.query || {};
      if (typeof q.first_name === "string" && q.first_name) this.form.first_name = q.first_name;
      if (typeof q.email === "string" && q.email) this.form.email = q.email;
    } catch {
      // ignore
    }
  },

  methods: {
    async submitForm(form) {
      try {
        this.$v.form.$touch();
        if (!this.$v.form.$invalid) {
          // check terms
          if (!this.terms) {
            toast(this, "accept_con", "error");
            return;
          }
          this.overlay = true;
          const { data } = await this.$http.post("register", form);
          if (data.status) {
            this.overlay = false;
            toast(this, data.message, "success");
            this.$router.push({
              path: "/verify-new-user",
              query: {
                email: form.email,
                ...(this.$route.query?.source ? { source: this.$route.query.source } : {}),
              },
            });
          }
        }
      } catch (error) {
        this.overlay = false;
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.ps-auth-form {
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ps-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}
@media (min-width: 640px) {
  .ps-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.ps-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ps-field__label {
  font-size: 0.875rem;
  font-weight: 900;
  color: var(--ps-ink);
}

.ps-field__pw {
  position: relative;
}
.ps-field__pw .ps-input {
  padding-right: 3rem;
}
.ps-field__eye {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--ps-slate-400);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}
.ps-field__eye:hover {
  color: var(--ps-brand);
}

.ps-field__error {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
}

.ps-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  width: fit-content;
}
.ps-check--wrap {
  max-width: 100%;
}

.ps-check__box {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: var(--ps-brand);
  cursor: pointer;
  flex-shrink: 0;
}

.ps-check__text {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--ps-slate-500);
  line-height: 1.6;
}

.ps-auth-submit {
  padding: 1rem;
}

.ps-auth-switch {
  margin: 24px 0 0;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--ps-slate-500);
}

.ps-auth-switch__link {
  color: var(--ps-brand);
  font-weight: 900;
  text-decoration: none;
}
.ps-auth-switch__link:hover {
  text-decoration: underline;
}
</style>
